import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarPlot, ChartsAxisHighlight, ChartsLegend, ChartsXAxis, ChartsYAxis } from '@mui/x-charts';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { orderBy } from 'lodash';
import { BoxWithHeader } from '../common';
import { DealerAccidentsOverviewProps } from './types';

export const AllDealersAccidentsCountOverview = ({ graphData, date }: DealerAccidentsOverviewProps) => {
  if (!graphData) return null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const acceptedCountsMap = new Map(
    graphData.totals.countAcceptedByAllDealers.values.map((item) => [item.id, item.count])
  );

  const sortedArray = orderBy(
    graphData.totals.countTotalByAllDealers.values,
    [(item) => acceptedCountsMap.get(item.id) ?? 0, (item) => item.count ?? 0],
    ['desc', 'desc']
  );

  const totalCounts = sortedArray.map((item) => item.count);
  const acceptedCounts = sortedArray.map((item) => acceptedCountsMap.get(item.id) ?? 0);

  const dealerLabels = sortedArray.map((item) => `${item.name}`);

  // --- layout podle zařízení
  const layout = isMobile ? 'horizontal' : 'vertical';

  const xAxis = isMobile
    ? [
        {
          id: 'x-axis-id',
          scaleType: 'linear'
        } as const
      ]
    : [
        {
          id: 'x-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ];

  const yAxis = isMobile
    ? [
        {
          id: 'y-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ]
    : [
        {
          id: 'y-axis-id',
          scaleType: 'linear'
        } as const
      ];

  const margin = isMobile
    ? { left: 45, right: 10, top: 20, bottom: 120 }
    : { left: 40, right: 20, top: 20, bottom: 150 };

  return (
    <BoxWithHeader
      title={
        <>
          <Typography variant="h6" fontWeight="bold">
            Přehled nehod u obchodníků
          </Typography>
          <Typography variant="body2" color="gray">
            {`Celkový počet řešených nehod v čase (${date})`}
          </Typography>
        </>
      }
    >
      <Box sx={{ width: '100%', height: 600 }}>
        <ResponsiveChartContainer
          skipAnimation
          margin={margin}
          series={[
            {
              type: 'bar',
              data: totalCounts,
              label: 'Nahlášené nehody',
              layout
            },
            {
              type: 'bar',
              data: acceptedCounts,
              color: 'green',
              label: 'Řešené nehody',
              layout
            }
          ]}
          xAxis={xAxis}
          yAxis={yAxis}
        >
          <BarPlot borderRadius={2} barLabel="value" />

          {/* X osa – na desktopu jména, na mobilu hodnoty */}
          <ChartsXAxis
            position="bottom"
            axisId="x-axis-id"
            label={isMobile ? 'Počet nehod' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 10
                  }
                : {
                    angle: -70,
                    textAnchor: 'end',
                    fontSize: 9
                  }
            }
          />

          {/* Y osa – na mobilu jména, na desktopu číselná osa */}
          <ChartsYAxis
            position="left"
            axisId="y-axis-id"
            label={!isMobile ? 'Počet nehod' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 9,
                    angle: -40
                  }
                : {
                    textAnchor: 'end',
                    fontSize: 9
                  }
            }
          />

          <ChartsAxisHighlight y="band" />
          <ChartsLegend position={{ vertical: 'bottom', horizontal: 'middle' }} />
        </ResponsiveChartContainer>
      </Box>
    </BoxWithHeader>
  );
};
